import { useContext, Fragment, ReactNode } from "react";
import { LangContext, LangContextType } from "../../../utilities/customHooks";
import { useSuspenseQuery } from "@tanstack/react-query";
import Button from "../../../Components/Button";
import { useNavigate } from "react-router-dom";
import textData from "../../../textData.json";
import { getLatestMembershipApplication } from "../../../services/founderService";
import * as T from "../../../utilities/frontendTypes";
import { queryKeys } from "../../../utilities/queryKeys";
import RoadmapCard from "../../../Components/RoadmapCard";
import DashboardPageLayout, { RoadmapSection } from "../../../Components/DashboardPageLayout";
import { MapPinIcon } from "../../../Components/icons/DashboardIcons";
import EducationSection from "../../../Components/EducationSection";
import ProgramDetailsFooter from "../../../Components/ProgramDetailsFooter";
import { formatOrdinalDate } from "../../../utilities/helperFunctions";
import { Alert, AlertTitle, AlertDescription } from "../../../Components/ui/alert";
import type { GenericDashboardDef } from "./dashboardRouteConfig";

type TextBlock = Record<string, Record<string, string>>;

const founderDashboards = (textData as Record<string, unknown>).Pages as Record<string, unknown>;
const dashboardText = (founderDashboards as Record<string, unknown>).Dashboards as Record<string, unknown>;
const allDashboards = (dashboardText as Record<string, unknown>).FounderDashboards as Record<string, TextBlock>;

function getText(textPrefix: string, key: string, langState: string): string {
  return allDashboards[textPrefix]?.[key]?.[langState] ?? "";
}

interface GenericFounderDashboardProps {
  config: GenericDashboardDef;
  companyNameForDashboard: string;
  isIntakeOpen?: boolean;
  intakeStatus?: T.IntakeStatus | null;
  midApplication?: boolean;
  /** Renders after button/alert in nextStepCont (e.g. info box for ProjectCompleted) */
  children?: ReactNode;
  /** Renders modal at bottom of layout (e.g. ExpertInvite assessment modal) */
  modalContent?: ReactNode;
  /** Overrides default button navigation — used for modal opens or mutations */
  onButtonClick?: () => void;
}

/** Wrapper that fetches latestApp via useSuspenseQuery, then renders GenericDashboardInner */
function GenericDashboardWithFetch(props: GenericFounderDashboardProps) {
  const { data: latestAppData } = useSuspenseQuery({
    queryKey: queryKeys.members.latestApplication(),
    queryFn: () => getLatestMembershipApplication(),
  });

  const latestApp = latestAppData.data as T.ApplicationDetails;
  return <GenericDashboardInner {...props} latestApp={latestApp} />;
}

/** Exported component — routes to WithFetch or Inner based on config.fetchLatestApp */
export default function GenericFounderDashboard(props: GenericFounderDashboardProps) {
  if (props.config.fetchLatestApp) {
    return <GenericDashboardWithFetch {...props} />;
  }
  return <GenericDashboardInner {...props} />;
}

function GenericDashboardInner({
  config,
  companyNameForDashboard,
  isIntakeOpen = true,
  intakeStatus,
  midApplication,
  children,
  modalContent,
  onButtonClick,
  latestApp,
}: GenericFounderDashboardProps & { latestApp?: T.ApplicationDetails }) {
  const navigate = useNavigate();
  const { langState } = useContext(LangContext) as LangContextType;

  const submissionDate = config.showSubmissionDate && latestApp
    ? new Date(latestApp.createdAt)
    : undefined;

  // Text helpers
  const t = (key: string, prefixOverride?: string) =>
    getText(prefixOverride ?? config.textPrefix, key, langState);

  // Reapply cooldown (rejected dashboards): show the eligible date while
  // blocked; once eligible (or auto-rejected), swap the CTA to "Apply again".
  const reapplyEligibility = config.reapplyAware ? latestApp?.reapplyEligibility : undefined;
  const reapplyCtaActive = Boolean(reapplyEligibility?.eligible);
  const reapplyEligibleDateText = reapplyEligibility && !reapplyEligibility.eligible && reapplyEligibility.eligibleDate
    ? new Date(reapplyEligibility.eligibleDate).toLocaleDateString(
        langState === "fr" ? "fr-CA" : "en-CA",
        { year: "numeric", month: "long", day: "numeric" },
      )
    : null;

  // Button text — supports midApplication alternate key
  const buttonText = reapplyCtaActive
    ? t("ReapplyNowButton")
    : midApplication && config.buttonKeyMidApp
      ? t(config.buttonKeyMidApp)
      : t(config.buttonKey, config.buttonTextPrefix);

  // Button route — supports midApplication alternate route
  const buttonRoute = reapplyCtaActive
    ? (midApplication ? "/dashboard/profile" : "/dashboard/application")
    : midApplication && config.buttonRouteMidApp
      ? config.buttonRouteMidApp
      : config.buttonRoute;

  // NextStep title
  const nextStepTitleKey = config.nextStepTitleKey ?? "NextStepTitle";
  const nextStepTitle = t(nextStepTitleKey, config.nextStepTitlePrefix);

  // Button handler
  const handleButtonClick = onButtonClick ?? (buttonRoute ? () => navigate(buttonRoute) : undefined);

  // Whether to show intake-closed alert instead of button — the reapply CTA
  // is always intake-gated (eligibility never overrides a closed intake)
  const showIntakeClosedAlert = (config.intakeGatedButton || reapplyCtaActive) && !isIntakeOpen;

  // Optional header inside the roadmap card (IO/NF approved layout):
  // description keys concatenated with NO separator + bolded date + suffix.
  const roadmapHeader = config.roadmapHeader && (
    <div className="flex gap-2">
      <div className="flex gap-2">
        <h4 className="mt-2">{config.roadmapHeader.descriptionKeys.map((key) => t(key)).join("")}</h4>
        {config.roadmapHeader.showSubmissionDate && latestApp && (
          <h4 className="font-semibold mt-2">{formatOrdinalDate(new Date(latestApp.createdAt))}{config.roadmapHeader.dateSuffix ?? ""}</h4>
        )}
      </div>
    </div>
  );

  return (
    <DashboardPageLayout>
      {config.portalTitleKey && <h4>{t(config.portalTitleKey)}</h4>}
      <div className="flex justify-between items-start self-stretch w-full">
        <RoadmapSection
          title={t("Roadmap")}
          hideTitle={config.hideRoadmapTitle}
          sizingClassName={config.roadmapSizingClassName}
          header={roadmapHeader}
        >
          {config.roadmapCards.map((card, i) => {
            const cardPrefix = card.textPrefixOverride ?? config.textPrefix;
            const title = getText(cardPrefix, card.titleKey, langState);
            const subtitle = card.subtitleKey ? getText(cardPrefix, card.subtitleKey, langState) : undefined;

            // Intake-gated clickable card: current when open, locked when closed
            const effectiveStatus = card.intakeGated && !isIntakeOpen ? "locked" : card.status;
            const showClick = card.clickRoute && (!card.intakeGated || isIntakeOpen);

            if (showClick) {
              return (
                <button
                  key={i}
                  onClick={() => navigate(card.clickRoute!)}
                  className="w-full text-left hover:underline hover:cursor-pointer hover:primaryButtonHover"
                >
                  <RoadmapCard status={effectiveStatus} title={title} subtitle={subtitle} />
                </button>
              );
            }

            return <RoadmapCard key={i} status={effectiveStatus} title={title} subtitle={subtitle} />;
          })}
        </RoadmapSection>
        <div className="oppositeRoadMap">
          {!config.hideNextStep && (
          <div className="nextStepCont">
            {config.showIcon ? (
              <div className="flex gap-2 nextStepFull">
                <div className="flex-col gap-2">
                  <h3 className="font-semibold">{nextStepTitle}</h3>
                  <h4 className="mt-2">
                    {config.descriptionKeys.map((key, i) => (
                      <Fragment key={i}>
                        {i > 0 && <br />}
                        {t(key)}
                      </Fragment>
                    ))}
                  </h4>
                </div>
                <MapPinIcon />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <h3 className="font-semibold">{nextStepTitle}</h3>
                {config.descriptionKeys.map((key, i) => (
                  <p key={i} className={`mt-2 bodyLarger${i === 0 && config.showReasonNote ? " font-semibold break-words" : ""}`}>{t(key)}</p>
                ))}
              </div>
            )}
            {config.bulletKeys && (
              <ul className="list-disc ml-8 mb-6">
                {config.bulletKeys.map((key, i) => (
                  <li key={i}>{t(key)}</li>
                ))}
              </ul>
            )}
            {submissionDate && (
              <h4 className="font-semibold mt-2">{formatOrdinalDate(submissionDate)}</h4>
            )}
            {config.showReasonNote && latestApp?.reasonNote && (
              <div className="mt-2 mb-4">
                {config.reasonNoteLabelKey && (
                  <p className="bodyLarger font-semibold">{t(config.reasonNoteLabelKey)}</p>
                )}
                <p className="mt-1">{latestApp.reasonNote}</p>
              </div>
            )}
            {config.showReasonNote && !latestApp?.reasonNote && config.reasonNoteFallbackKey && (
              // No admin note on the application (e.g. the admin skipped the
              // optional reason) — generic copy keeps the card from collapsing
              // to a bare title + button.
              <p className="mt-2 mb-4 bodyLarger">{t(config.reasonNoteFallbackKey)}</p>
            )}
            {reapplyEligibleDateText && (
              <p className="mt-2 mb-4 bodyLarger font-semibold">
                {t("ReapplyEligibleOn").replace("{date}", reapplyEligibleDateText)}
              </p>
            )}
            {reapplyCtaActive && (
              <p className="mt-2 mb-4 bodyLarger">{t("ReapplyNowNextStep")}</p>
            )}
            {config.inTouchKeys && (
              <span className="mt-4 mb-6">
                {config.inTouchKeys.map((key, i) => (
                  <span key={i}>
                    {i > 0 && <br />}
                    {t(key)}
                  </span>
                ))}
              </span>
            )}
            {showIntakeClosedAlert ? (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTitle className="text-base font-semibold">
                  {intakeStatus?.closedTitle?.[langState as "eng" | "fr"] || intakeStatus?.closedTitle?.eng || (langState === "fr" ? "Mise à jour du programme" : "Program Update")}
                </AlertTitle>
                <AlertDescription className="whitespace-pre-wrap">
                  {intakeStatus?.closedMessage?.[langState as "eng" | "fr"] || intakeStatus?.closedMessage?.eng || ""}
                </AlertDescription>
              </Alert>
            ) : (
              <Button
                buttonText={buttonText}
                buttonType="primaryFull"
                disabled={config.buttonDisabled}
                actionHandler={handleButtonClick ?? (() => {})}
              />
            )}
            {children}
          </div>
          )}
          {config.education && (
            <EducationSection
              textPrefix={config.education.textPrefix}
              bulletTextPrefix={config.education.bulletTextPrefix}
              buttonTextPrefix={config.education.buttonTextPrefix}
            />
          )}
          {config.programDetailsTextPrefix && config.programDetailsPlacement === "sidebar" && (
            <ProgramDetailsFooter textPrefix={config.programDetailsTextPrefix} variant="sidebar" />
          )}
        </div>
      </div>
      {config.programDetailsTextPrefix && config.programDetailsPlacement !== "sidebar" && (
        <ProgramDetailsFooter textPrefix={config.programDetailsTextPrefix} />
      )}
      {modalContent}
    </DashboardPageLayout>
  );
}
